import {FormControl, FormField, openConfirmDialog} from 'platform/components';

import {UseFormReturn} from 'react-hook-form';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {handleFormReset, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {createVehicleTitle} from 'features/vehicle-catalogue';

import {MODEL_FAMILY_OTHER} from '../../../constants/makeModel';
import {RentalVehicleFormType} from '../../../types/RentalVehicleFormType';
import {useVehicleMake} from '../hooks/useVehicleMake';
import {useVehicleModel} from '../hooks/useVehicleModel';

interface CreatableModelSelectProps extends RequiredTestIdProps {
  control: FormControl<RentalVehicleFormType>;
  formApi: UseFormReturn<RentalVehicleFormType>;
  vehicleType: VehicleTypeEnumObject;
}

export function CreatableModelSelect(props: CreatableModelSelectProps) {
  const make = props.formApi.watch('formMake');
  const unknownMakes = props.formApi.watch('unknownMakes');

  const unknownModels = props.formApi.watch('unknownModels');

  const {
    groupedModelOptions,
    detail: {globalModels, flatModels},
    isLoading,
  } = useVehicleModel({
    vehicleType: props.vehicleType,
    make,
    unknownModels,
  });

  const {
    detail: {flatMakes},
  } = useVehicleMake({
    vehicleType: props.vehicleType,
    unknownMakes,
  });

  const handleChange = (newFormModelFamily: string | Nullish) => {
    const newTitle = createVehicleTitle({
      flatMakes,
      flatModels,
      formMake: make,
      formModel: newFormModelFamily,
    });

    handleFormReset<RentalVehicleFormType>(props.formApi, [
      {name: 'trim', value: null},
      {name: 'title', value: newTitle},
      {name: 'variant', value: null},
      {name: 'engineName', value: null},
      {name: 'engineCode', value: null},
      {name: 'power', value: null},
      {name: 'engineVolume', value: null},
    ]);

    if (!newFormModelFamily) {
      return;
    }

    if (globalModels.some((item) => item.value === newFormModelFamily)) {
      props.formApi.setValue('modelFamily', newFormModelFamily);
      props.formApi.setValue('customModelFamily', null);
      return;
    }

    props.formApi.setValue('modelFamily', MODEL_FAMILY_OTHER);
    props.formApi.setValue('customModelFamily', newFormModelFamily);
  };

  return (
    <FormField
      type="creatableChoice"
      control={props.control}
      name="formModelFamily"
      label={i18n.t('entity.vehicle.labels.model')}
      isLoading={isLoading}
      options={groupedModelOptions}
      onChange={(newValue) => handleChange(newValue as string | Nullish)}
      onCreateOption={(newValue) => {
        openConfirmDialog({
          text: i18n.t('entity.vehicle.labels.createCustomModelConfirm', {model: newValue}),
          onConfirm: () => {
            props.formApi.setValue('formModelFamily', newValue);
            props.formApi.setValue('unknownModels', [{label: newValue, value: newValue}]);
            handleChange(newValue);
          },
        });
      }}
      isDisabled={!make}
      isRequired
      data-testid={suffixTestId('model', props)}
    />
  );
}
